import { useState } from "react";
import { Box, Paper, Text } from "@mantine/core";
import CountryFlag, { countryHasRenderableFlag } from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import corridorCardClasses from "@/components/ecosystem-head/EcosystemHeadCorridorCard.module.css";
import { CorridorCardThumbnail } from "@/components/ecosystem-head/EcosystemHeadCorridorCard";
import { ColoredIconChip, FocusSectorChip } from "@/components/ecosystem-head/FocusSectorChip";
import { iconIdForPlatformSector } from "@/components/ecosystem-head/focusSectorIcon.util";
import { getSectorIcon, getSectorVisual } from "@/pages/home/sectorCardVisual.util";
import {
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import { comingSoonCorridorHasFlags } from "@/shared/constants/comingSoonEcosystemCorridors.const";

const CARD_BLUE = "#2563EB";

type EcosystemCorridorPlaceholderCardProps = {
  corridor: ComingSoonEcosystemCorridor;
  /** Fixed width for horizontal scroll layouts (e.g. explore page). */
  fixedWidth?: number;
  fullWidth?: boolean;
  /** Use compact flag badges (sector carousel) vs inline flags (explore grid). */
  variant?: "carousel" | "explore";
  compact?: boolean;
  /** Render inside CorridorCarouselParentCard — no outer chrome / aspect ratio. */
  nested?: boolean;
};

function PlaceholderHeaderFlags({
  corridor,
  compact = false,
}: {
  corridor: ComingSoonEcosystemCorridor;
  compact?: boolean;
}) {
  const originCountry = corridor.country?.trim() || null;
  const targetCountry = corridor.targetCountry?.trim() || null;
  const flagSize = compact ? "sm" : "md";

  const showFlags =
    comingSoonCorridorHasFlags(corridor) &&
    Boolean(originCountry && countryHasRenderableFlag(originCountry)) &&
    Boolean(targetCountry && countryHasRenderableFlag(targetCountry));

  if (!showFlags || !originCountry || !targetCountry) return null;

  return (
    <Box className={corridorCardClasses.flagPair}>
      <CountryFlag country={originCountry} size={flagSize} />
      <CorridorDoubleArrow size={compact ? 10 : 12} color={CARD_BLUE} />
      <CountryFlag country={targetCountry} size={flagSize} />
    </Box>
  );
}

export default function EcosystemCorridorPlaceholderCard({
  corridor,
  fixedWidth,
  fullWidth = false,
  variant = "carousel",
  compact = false,
  nested = false,
}: EcosystemCorridorPlaceholderCardProps) {
  const isUnderway = corridor.status === "underway";
  const statusLabel = isUnderway ? "Underway" : "Coming Soon";
  const isExplore = variant === "explore";
  const cardHeight = ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT;
  const cardWidth = nested
    ? undefined
    : fullWidth && isExplore
      ? ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH
      : isExplore
        ? ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH
        : fixedWidth;
  const [isHovered, setIsHovered] = useState(false);
  const corridorTitle = corridor.corridorSubtitle.trim();
  const industrySubtitle = corridor.industryLabel.trim();
  const sectorIconId = iconIdForPlatformSector(corridor.sectorSlug);
  const sectorVisual = getSectorVisual(corridor.sectorSlug);
  const SectorIcon = getSectorIcon(corridor.sectorSlug);

  return (
    <Paper
      radius={nested ? "md" : "lg"}
      shadow="none"
      className={`${corridorCardClasses.card} ${compact ? corridorCardClasses.compact : ""} ${nested ? corridorCardClasses.nested : ""}`}
      onMouseEnter={isExplore ? () => setIsHovered(true) : undefined}
      onMouseLeave={isExplore ? () => setIsHovered(false) : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        border: nested
          ? "1px solid transparent"
          : `1px solid ${isExplore && isHovered ? "#93C5FD" : "#E2E8F0"}`,
        overflow: "hidden",
        boxShadow: nested
          ? "none"
          : isExplore
            ? isHovered
              ? "0 12px 28px rgba(4, 48, 92, 0.14)"
              : "0 4px 14px rgba(15, 23, 42, 0.06)"
            : "0 4px 14px rgba(15, 23, 42, 0.06)",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
        height: nested ? "100%" : "100%",
        minHeight: nested ? 0 : ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
        flex: nested ? 1 : "0 0 auto",
        ...(nested
          ? {}
          : compact
            ? {
                height: fullWidth ? "100%" : cardHeight,
                minHeight: fullWidth ? 0 : cardHeight,
              }
            : {}),
        ...(cardWidth
          ? { width: cardWidth, maxWidth: "100%" }
          : { width: "100%", maxWidth: "100%" }),
      }}
    >
      <Box className={corridorCardClasses.cardContent}>
        <Box className={corridorCardClasses.header}>
          <CorridorCardThumbnail title={corridorTitle} />
          <PlaceholderHeaderFlags corridor={corridor} compact={compact} />
        </Box>

        <Box className={corridorCardClasses.body}>
          <Text
            className={corridorCardClasses.title}
            fw={800}
            c="#0F172A"
            lh={1.3}
          >
            {corridorTitle}
          </Text>
          {industrySubtitle ? (
            <Text
              className={corridorCardClasses.subtitle}
              fz={compact ? 11 : 13}
              fw={500}
              c="#64748B"
              lh={1.35}
            >
              {industrySubtitle}
            </Text>
          ) : null}
        </Box>

        <Box className={corridorCardClasses.chips}>
          {industrySubtitle ? (
            <ColoredIconChip
              label={industrySubtitle}
              Icon={SectorIcon}
              visual={sectorVisual}
              size={compact ? "sm" : "md"}
              strong
            />
          ) : null}
          <Box w="100%">
            <Text className={corridorCardClasses.focusLabel} fw={800} mb={4}>
              Focus sectors
            </Text>
            <Box className={corridorCardClasses.focusWrap}>
              <FocusSectorChip
                label={industrySubtitle || "Launching soon"}
                icon={sectorIconId}
                size="sm"
              />
            </Box>
          </Box>
        </Box>

        <Box className={corridorCardClasses.footer}>
          <Box className={corridorCardClasses.ctaRow}>
            <Box className={corridorCardClasses.ctaComingSoon}>{statusLabel}</Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
